<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from 'vue'
import {useI18n} from "vue-i18n";

const AUTOPLAY_DELAY = 5000
const PROGRESS_UPDATE_INTERVAL = 50

const {locale} = useI18n()

const slides = computed(() =>
    [
      {
        id: 1,
        title: 'Интернет для платёжных терминалов PayNet',
        description: 'Стабильное подключение для работы платёжных терминалов',
        image: `/img/mb/paynet.png`,
        background: 'banner-bg-paynet',
        buttons: [
          {
            label: 'Подробнее',
            variant: 'secondary',
            href: `/${locale.value}/katalog-uslug/internet-dlya-platezhnyh-terminalov-paynet?utm_source=b2b-telecom&utm_medium=banner&utm_campaign=main-page`,
            'arrow': true
          }
        ]
      },
      {
        id: 2,
        title: 'banner.docs.title',
        description: 'banner.docs.description',
        image: `/img/mb/docs.png`,
        background: 'banner-bg-docs',
        buttons: [
          {
            label: 'banner.docs.button',
            variant: 'secondary',
            href: `${import.meta.env.VITE_DOCS_HOST}?lang=${locale.value}&utm_source=b2b-telecom&utm_medium=banner&utm_campaign=main_page`,
            'arrow': true
          }
        ]
      },
      {
        id: 3,
        title: 'Оплата по QR на столе, заказы и бронирование',
        description: 'Подключите Halyk Рестораны без абонентской платы и обслуживайте гостей быстрее',
        image: `/img/mb/halykservice.png`,
        background: 'banner-bg-halykservice',
        buttons: [
          {
            label: 'Узнать о сервисе',
            variant: 'secondary',
            href: `https://halykbank.kz${locale.value === 'ru' ? '' : locale.value === 'kk' ? '/kz' : ('/' + locale.value)}/business/payment/halyk-restorany?utm_source=kazakhtelecom`,
            'arrow': true
          }
        ]
      },
      {
        id: 4,
        title: 'Телевидение нового поколения для бизнеса',
        description: 'Простое подключение, богатый выбор контента',
        image: '/img/mb/television.png',
        background: 'banner-bg-tv',
        buttons: [
          {
            label: 'Подробнее',
            variant: 'secondary',
            href: `${import.meta.env.VITE_HOST}/${locale.value === 'en' ? 'kk' : locale.value}/katalog-uslug/tv-plus-biznes?utm_source=b2b-telecom&utm_medium=banner&utm_campaign=main_page`,
            'arrow': true
          }
        ]
      },
      {
        id: 5,
        title: 'Магазинам и аптекам\nвыгоднее с OnlineDuken',
        description: 'Заказывайте товары у проверенных поставщиков, получайте до 50 000 бонусов в месяц, оплачивайте покупку сразу или с отсрочкой на сумму до 5 млн ₸',
        image: `/img/mb/onlineDuken.png`,
        background: 'banner-bg-online-duken',
        buttons: [
          {
            label: 'Оставить заявку',
            variant: 'secondary',
            href: `https://halykbank.kz${locale.value === 'ru' ? '' : locale.value === 'kk' ? '/kz' : ('/' + locale.value)}/business/other/onlineduken?utm_source=kazakhtelecom`,
            'arrow': true
          }
        ]
      },
      {
        id: 6,
        title: 'Ваш бизнес в одной ссылке',
        description: 'Соберите контакты, соцсети, сайт и предложения в одну страницу, которой удобно делиться с клиентами',
        image: `/img/mb/tap_${locale.value}.png`,
        background: 'banner-bg-tap',
        buttons: [
          {
            label: 'Создать страницу',
            variant: 'secondary',
            href: `${import.meta.env.VITE_TAP_HOST}?lang=${locale.value}&utm_source=b2b-telecom&utm_medium=banner&utm_campaign=main_page`,
            'arrow': true
          }
        ]
      }
    ]
)

const activeIndex = ref(0)
const progress = ref(0)

let autoplayTimer = null
let progressTimer = null

let startedAt = 0
let elapsedBeforePause = 0

const hasSlides = computed(() => slides.value.length > 0)
const hasControls = computed(() => slides.value.length > 1)
const currentSlide = computed(() => slides.value[activeIndex.value] || null)

const clearTimers = () => {
  if (autoplayTimer) {
    clearTimeout(autoplayTimer)
    autoplayTimer = null
  }

  if (progressTimer) {
    clearInterval(progressTimer)
    progressTimer = null
  }
}

const updateProgressFromTime = () => {
  const now = Date.now()
  const elapsed = elapsedBeforePause + (startedAt ? now - startedAt : 0)
  progress.value = Math.min((elapsed / AUTOPLAY_DELAY) * 100, 100)
}

const startAutoplay = (reset = false) => {
  const shouldReset = reset === true

  if (slides.value.length <= 1) return

  clearTimers()

  if (shouldReset) {
    progress.value = 0
    elapsedBeforePause = 0
  }

  startedAt = Date.now()

  const remainingDelay = Math.max(AUTOPLAY_DELAY - elapsedBeforePause, 0)

  if (remainingDelay <= 0) {
    nextSlide(false)
    return
  }

  progressTimer = setInterval(() => {
    updateProgressFromTime()
  }, PROGRESS_UPDATE_INTERVAL)

  autoplayTimer = setTimeout(() => {
    nextSlide(false)
  }, remainingDelay)
}

const stopAutoplay = () => {
  if (slides.value.length <= 1) return

  if (startedAt) {
    elapsedBeforePause += Date.now() - startedAt
    startedAt = 0
  }

  updateProgressFromTime()
  clearTimers()
}

const restartAutoplay = () => {
  startAutoplay(true)
}

const goToSlide = (index) => {
  if (!slides.value.length || index === activeIndex.value) return

  activeIndex.value = index
  elapsedBeforePause = 0
  progress.value = 0
  restartAutoplay()
}

const nextSlide = (restart = true) => {
  if (!slides.value.length) return

  activeIndex.value = (activeIndex.value + 1) % slides.value.length
  elapsedBeforePause = 0
  progress.value = 0
  startedAt = 0

  if (restart) {
    restartAutoplay()
  } else {
    startAutoplay(true)
  }
}

const prevSlide = () => {
  if (!slides.value.length) return

  activeIndex.value = (activeIndex.value - 1 + slides.value.length) % slides.value.length
  elapsedBeforePause = 0
  progress.value = 0
  startedAt = 0
  restartAutoplay()
}

const SWIPE_THRESHOLD = 50
let touchStartX = 0
let touchStartY = 0

const onTouchStart = (e) => {
  if (!hasControls.value) return
  touchStartX = e.changedTouches[0].clientX
  touchStartY = e.changedTouches[0].clientY
  stopAutoplay()
}

const onTouchEnd = (e) => {
  if (!hasControls.value) return

  const dx = e.changedTouches[0].clientX - touchStartX
  const dy = e.changedTouches[0].clientY - touchStartY

  if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
    if (dx < 0) {
      activeIndex.value = (activeIndex.value + 1) % slides.value.length
    } else {
      prevSlide()
    }
  }

  startAutoplay()
}

onMounted(() => {
  startAutoplay(true)
})

onBeforeUnmount(() => {
  clearTimers()
})
</script>

<template>
  <section
      v-if="hasSlides"
      class="banner-slider"
      :class="{ [currentSlide.background]: true }"
      @mouseenter="stopAutoplay"
      @mouseleave="startAutoplay"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
  >
    <div class="banner-slider__inner">
      <div class="banner-slider__slides">
        <div
            v-for="(slide, index) in slides"
            :key="slide.id"
            class="banner-slider__slide"
            :class="{ 'banner-slider__slide--active': index === activeIndex }"
        >
          <div class="banner-slider__content">
            <div class="banner-slider__text">
              <h2 class="banner-slider__title">
                {{ $t(slide.title) }}
              </h2>

              <p
                  v-if="slide.description"
                  class="banner-slider__description"
              >
                {{ $t(slide.description) }}
              </p>
            </div>

            <div
                v-if="slide.buttons?.length"
                class="banner-slider__actions"
            >
              <a
                  v-for="btn in slide.buttons"
                  :key="btn.label"
                  :href="btn.href || '#'"
                  class="banner-slider__button"
                  :class="`banner-slider__button--${btn.variant}`"
              >
                <span>{{ $t(btn.label) }}</span>

                <div v-if="btn.arrow" class="arrow">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_835_14897)">
                      <path
                          d="M5 13.0002H16.17L11.29 17.8802C10.9 18.2702 10.9 18.9102 11.29 19.3002C11.68 19.6902 12.31 19.6902 12.7 19.3002L19.29 12.7102C19.68 12.3202 19.68 11.6902 19.29 11.3002L12.71 4.70021C12.32 4.31021 11.69 4.31021 11.3 4.70021C10.91 5.09021 10.91 5.72022 11.3 6.11022L16.17 11.0002H5C4.45 11.0002 4 11.4502 4 12.0002C4 12.5502 4.45 13.0002 5 13.0002Z"
                          fill="#6C757D"/>
                    </g>
                  </svg>
                </div>
              </a>
            </div>
          </div>

          <div class="banner-slider__media">
            <img
                :src="slide.image"
                :alt="$t(slide.title)"
                class="banner-slider__image"
            >
          </div>
        </div>
      </div>
    </div>
    <div class="banner-slider__bottom_wrapper">
      <div class="banner-slider__bottom">
        <button
            v-for="(slide, index) in slides"
            :key="slide.id"
            type="button"
            class="banner-slider__indicator"
            :aria-label="`Перейти к слайду ${index + 1}`"
            @mouseenter="goToSlide(index)"
            @focus="goToSlide(index)"
        >
          <span
              class="banner-slider__indicator-fill"
              :style="{
              width: `${index < activeIndex ? 100 : index === activeIndex ? progress : 0}%`
            }"
          />
        </button>
      </div>
    </div>
    <div v-if="hasControls" class="banner-slider__controls">
      <button class="banner-slider__nav prev" @click="prevSlide">
        <img src="/img/slider/chevron_left.svg" alt="preview">
      </button>
      <button class="banner-slider__nav next" @click="nextSlide">
        <img src="/img/slider/chevron_left.svg" alt="next">
      </button>
    </div>
  </section>
</template>

<style scoped lang="scss">
.banner-slider {
  width: 100%;
  min-height: 450px;
  border-radius: 0 0 40px 40px;
  overflow: hidden;
  position: relative;
  z-index: 1;
  background-color: #fff !important;
  display: flex;
  align-items: center;

  &__inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
    position: relative;
    display: block;
  }

  &__slides {
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
  }

  &__slide {
    grid-area: 1 / 1;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 450px;
    gap: 24px;
    align-items: center;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.3s ease, visibility 0.3s ease;

    &--active {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
    }
  }

  &__bottom {
    display: flex;
    align-items: center;
    gap: 8px;
    position: absolute;
    bottom: 24px;
    width: 20%;
    z-index: 1;

    &_wrapper {
      width: 100%;
      display: flex;
      justify-content: center;
      position: absolute;
      bottom: 0;
      left: 0;
    }
  }

  &__indicator {
    position: relative;
    flex: 1;
    height: 4px;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.35);
    overflow: hidden;
    padding: 0;
    cursor: pointer;
  }

  &__indicator-fill {
    position: absolute;
    inset: 0 auto 0 0;
    width: 0;
    height: 100%;
    border-radius: inherit;
    background: #ffffff;
    transition: width 0.05s linear;
  }

  &__content {
    max-width: 100%;
  }

  &__title {
    font-size: 48px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 18px;
    line-height: 100%;
  }

  &__description {
    font-size: 18px;
    color: $color-primary;
  }

  &__actions {
    margin-top: 28px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__button {
    padding: 16px 20px 16px 24px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;

    span {
      font-size: 18px;
      line-height: 24px;
      font-weight: 500;
    }

    &:hover {
      gap: 13px;
      padding: 16px 15px 16px 24px;
    }

    &--primary {
      background: #246FF7;
      color: $color-primary;
      transition: $default-transition;

      .arrow {
        height: 24px;

        svg {
          path {
            fill: $color-primary;
          }
        }
      }

      &:hover {
        opacity: 0.6;
      }
    }

    &--secondary {
      background: $color-primary;
      color: $primary-dark;
      transition: $default-transition;

      .arrow {
        height: 24px;
      }

      &:hover {
        opacity: 0.9;
      }
    }
  }

  &__media {
    display: flex;
    justify-content: flex-end;
    user-select: none;
  }

  &__image {
    max-width: 100%;
    max-height: 360px;
    object-fit: contain;
  }

  &__controls {
    position: absolute;
    right: 0;
    bottom: calc(50% - 20px);
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0 24px;
  }

  &__nav {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: #fff;
    cursor: pointer;
    transition: $default-transition;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.25);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;

    &:hover {
      opacity: 1 !important;
      box-shadow: 0 6px 14px rgba(0, 0, 0, 0.16);
    }

    &.next {
      transform: rotate(180deg);
    }
  }

  &:hover {
    .banner-slider__nav {
      opacity: 0.45;
      visibility: visible;
      pointer-events: auto;
    }
  }
}

@media (max-width: 1440px) {
  .banner-slider {
    &__inner {
      max-width: 1158px;
    }
  }

}

@media (max-width: 1280px) {
  .banner-slider {
    &__inner {
      max-width: 1005px;
    }

    &__title {
      font-size: 36px;
    }

    &__description {
      font-size: 16px;
    }
  }
}

@media (max-width: 1024px) {
  .banner-slider {
    &__inner {
      max-width: 844px;
    }

    &__slide {
      grid-template-columns: minmax(0, 1fr) 320px;
    }

    &__title {
      margin-bottom: 16px;
    }

    .banner-slider__nav:hover {
      opacity: 1;
    }
  }

  .banner-slider.banner-bg-docs {
    background:
        radial-gradient(circle at 80% 50%, rgba(0, 210, 220, 0.85) 0%, transparent 45%),
        radial-gradient(circle at 65% 90%, rgba(150, 180, 255, 0.65) 0%, transparent 38%),
        radial-gradient(circle at 80% 15%, rgba(25, 35, 190, 0.75) 0%, transparent 45%),
        linear-gradient(135deg, #0960c4 0%, #006fd0 45%, #1038bb 100%);
  }
}

@media (max-width: 992px) {
  .banner-slider {
    &__slide {
      grid-template-columns: 1fr;
    }

    &__title {
      font-size: 42px;
    }
  }
}

@media (max-width: 700px) {
  .banner-slider {
    border-radius: 0 0 16px 16px;

    &__inner {
      padding: 32px;
    }

    &__slide {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 0;
      padding-bottom: 0;
    }

    &__bottom_wrapper {
      display: none;
    }

    &__content {
      max-width: none;
      display: contents;
    }

    &__text {
      min-height: 104px;
    }

    &__title {
      margin: 0 0 12px;
      font-size: 24px;
      line-height: 1.24;
      font-weight: 600;
      order: 1;
    }

    &__description {
      margin: 0;
      font-size: 14px;
      line-height: 1.1;
      color: #fff;
      order: 2;
    }

    &__media {
      width: 100%;
      margin: 24px 0 0;
      justify-content: center;
      order: 3;
    }

    &__image {
      width: 100%;
      max-width: 330px;
      max-height: 190px;
      object-fit: contain;
    }

    &__actions {
      width: 100%;
      margin: 8px 0 0;
      display: flex;
      flex-direction: column;
      gap: 12px;
      order: 4;
    }

    &__button {
      width: 100%;
      min-height: 40px;
      padding: 0 24px;
      border-radius: 8px;
      justify-content: center;
      box-sizing: border-box;

      span {
        font-size: 16px;
        line-height: 1;
      }

      .arrow {
        width: 24px;
        height: 24px;

        svg {
          width: 24px;
          height: 24px;
        }
      }

      &--primary {
        background: #2877f5;
      }

      &--secondary {
        background: #fff;
      }
    }

    &__controls {
      display: none;
    }
  }

  .banner-slider.banner-bg-docs {
    background:
        radial-gradient(circle at 50% 60%, rgba(0, 210, 220, 0.85) 0%, transparent 45%),
        radial-gradient(circle at 65% 90%, rgba(150, 180, 255, 0.65) 0%, transparent 38%),
        radial-gradient(circle at 80% 15%, rgba(25, 35, 190, 0.75) 0%, transparent 45%),
        linear-gradient(135deg, #0960c4 0%, #006fd0 45%, #1038bb 100%);
  }
}

.banner-bg-tv {
  background: linear-gradient(90deg, #0D87EF 0%, #1447E6 50%, #372AAC 100%);
}

.banner-bg-tap {
  background: linear-gradient(135deg, #0682F4 0%, #118CF9 50%, #83CFFB 100%);
}

.banner-bg-halykservice {
  background: linear-gradient(135deg, #003C3A 0%, #0A7375 50%, #005B5C 100%);
}

.banner-bg-paynet {
  background: linear-gradient(265deg, #4E99E5 1.72%, #1382FB 53.22%, #0037BA 100%);
}

.banner-bg-online-duken {
  background: linear-gradient(135deg, #4A5060 0%, #576070 50%, #4A5060 100%);
}

.banner-bg-docs {
  background:
      radial-gradient(circle at 75% 65%, rgba(0, 210, 220, 0.85) 0%, transparent 45%),
      radial-gradient(circle at 85% 90%, rgba(150, 180, 255, 0.65) 0%, transparent 38%),
      radial-gradient(circle at 80% 15%, rgba(25, 35, 190, 0.75) 0%, transparent 45%),
      linear-gradient(135deg, #0960c4 0%, #006fd0 45%, #1038bb 100%);
}
</style>